/**
 * 리치 문서(서식 있는 본문) — 화면 쪽 모양·상한·저장 준비 (v2.124.0 · 2026-09-04)
 *
 * 편집기(`components/RichEditor.tsx`)가 내놓는 것은 HTML 이 아니라 **문서 나무(JSON)** 다.
 * 서버(`backend/src/lib/rich-doc/schema.ts`)가 허용 목록으로 걸러 저장하고, HTML 은 서버가
 * 우리 태그만으로 새로 짓는다. 그래서 화면은 HTML 을 만들지도, 보내지도 않는다.
 *
 * ⚠**상한은 서버(`backend/src/lib/rich-doc/schema.ts`)와 같은 값이어야 한다.**
 *   어긋나면 「화면에서는 되는데 저장이 거절된다」가 된다 — `check:richdoc` ⑧ 이 앞뒤 값을 대조한다.
 */

/** 편집기가 다루는 문서 — 서버 스키마와 같은 모양(구조는 서버가 다시 검사한다) */
export interface RichDoc {
  type: 'doc';
  content: RichNode[];
}

export interface RichNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: RichNode[];
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  text?: string;
}

export const EMPTY_DOC: RichDoc = { type: 'doc', content: [] };

export interface RichDocLimits {
  maxTextChars: number;
  maxNodes: number;
  maxTables: number;
  maxTableRows: number;
  maxTableCols: number;
  maxCellChars: number;
  maxImages: number;
  maxListDepth: number;
}

/** 회의록 — 칸 수 8은 A4 한계 (서버와 같은 값) */
export const MEETING_DOC_LIMITS: RichDocLimits = {
  maxTextChars: 40_000, maxNodes: 4_000, maxTables: 5, maxTableRows: 50, maxTableCols: 8,
  maxCellChars: 200, maxImages: 10, maxListDepth: 3,
};

/**
 * 메일 본문 (서버와 같은 값).
 * ⭐⭐⭐**꼬리말 몫이 더해져 있다**(v2.125.0) — 작성 창은 본문 끝에 [메일 꼬리말]을 붙여 보내므로
 *   여기 재는 문서는 「사람이 쓴 본문 + 꼬리말」이다. 사람 몫만 재면 꼬리말을 등록한 사람만
 *   본문을 덜 쓸 수 있게 된다. 사람 몫 = 표 10 · 그림 10 · 글자 100,000 · 마디 8,000.
 */
export const MAIL_DOC_LIMITS: RichDocLimits = {
  // 그림 21 = 사람 몫 10 + 꼬리말(개인 부분) 몫 6 + 회사 정보 표 5 (v2.170.0 에서 16 · v2.211.0 에서 21)
  // 글자·마디·표도 회사 정보 표 몫(1,000 · 100 · 1)을 더했다 (v2.211.0)
  maxTextChars: 103_000, maxNodes: 8_400, maxTables: 12, maxTableRows: 200, maxTableCols: 12,
  // ⭐칸 글자 4,000 (v2.190.0) — 서버 MAIL_DOC_LIMITS 와 **같은 값**이어야 한다.
  //   딜등록 안내 메일의 Description 칸이 길다(운영 최대 2,312자). 어긋나면 「화면은 되는데 보내기가 거절」.
  maxCellChars: 4_000, maxImages: 21, maxListDepth: 4,
};

/** 메일 꼬리말 — 마이페이지에서 등록, 보내는 메일마다 따라 나간다 (서버와 같은 값) */
export const MAIL_SIGNATURE_LIMITS: RichDocLimits = {
  // 칸 260자 = 법적 고지문 한 칸(운영 실측 237자) · 그림 6 = 로고 1 + SNS 아이콘 4 + 여유 1
  maxTextChars: 2_000, maxNodes: 300, maxTables: 1, maxTableRows: 10, maxTableCols: 4,
  maxCellChars: 260, maxImages: 6, maxListDepth: 2,
};

/** 개발 계획 글 — 캡처 화면을 여러 장 붙이므로 그림 상한이 크다 (서버와 같은 값) */
export const DEV_PLAN_DOC_LIMITS: RichDocLimits = {
  maxTextChars: 40_000, maxNodes: 4_000, maxTables: 5, maxTableRows: 50, maxTableCols: 8,
  maxCellChars: 500, maxImages: 20, maxListDepth: 4,
};

/** 그림 폭(%) — 서버 meeting-image.ts 와 같은 값 */
export const MIN_IMAGE_WIDTH_PCT = 20;
export const MAX_IMAGE_WIDTH_PCT = 100;

/**
 * 편집기 본문 폭의 기준 픽셀 — 붙여넣은 그림이 이보다 작으면 **원래 크기 그대로** 보이도록
 * 백분율을 잡는다(작은 아이콘 하나가 본문을 꽉 채우면 우스꽝스럽다).
 */
export const BODY_REF_PX = 620;

function walk(node: RichNode, fn: (n: RichNode) => RichNode): RichNode {
  const next = fn(node);
  if (!next.content) return next;
  return { ...next, content: next.content.map((c) => walk(c, fn)) };
}

/**
 * 저장·발송용으로 다듬는다.
 *   · 새 그림(`src` 가 `data:`) → `dataUrl` 로 옮겨 싣는다. 서버가 바이트를 꺼내 담는다.
 *   · 저장된 그림(imageId > 0) → **주소(src)는 뺀다.** 서버는 번호만 안다.
 * ⭐`src` 는 화면에 그리기 위한 것일 뿐 문서의 일부가 아니다 — 보내면 서버가 버린다.
 */
export function docForSave(doc: RichDoc): RichDoc {
  const content = doc.content.map((n) => walk(n, (node) => {
    if (node.type !== 'image') return node;
    const a = node.attrs ?? {};
    const src = typeof a.src === 'string' ? a.src : '';
    const imageId = Number(a.imageId) || 0;
    const { src: _s, dataUrl: _d, ...rest } = a;
    if (imageId > 0) return { ...node, attrs: { ...rest, imageId } };
    if (src.startsWith('data:')) return { ...node, attrs: { ...rest, imageId: 0, dataUrl: src } };
    // 주소도 번호도 없는 그림 — 남길 것이 없다
    return { ...node, attrs: { ...rest, imageId: 0 } };
  }));
  return { type: 'doc', content };
}

/**
 * 서버에서 받은 문서를 편집기에 넣을 모양으로 — 저장된 그림 번호에 **볼 수 있는 주소**를 붙인다.
 * 주소는 부르는 쪽이 정한다(회의록은 `meetingImageUrl`, 메일은 저장된 그림이 없다).
 */
export function docForEdit(doc: RichDoc | null | undefined, imageUrl: (imageId: number) => string): RichDoc {
  if (!doc || !Array.isArray(doc.content)) return EMPTY_DOC;
  const content = doc.content.map((n) => walk(n, (node) => {
    if (node.type !== 'image') return node;
    const a = node.attrs ?? {};
    const imageId = Number(a.imageId) || 0;
    if (imageId > 0) return { ...node, attrs: { ...a, src: imageUrl(imageId) } };
    return node;
  }));
  return { type: 'doc', content };
}

/** 글자만 있는 문서 — 줄마다 문단 하나 (메일 창을 미리 채운 글 등) */
export function textToRichDoc(text: string): RichDoc {
  const t = text.replace(/\r\n?/g, '\n').trimEnd();
  if (!t.trim()) return EMPTY_DOC;
  return {
    type: 'doc',
    content: t.split('\n').map((line): RichNode => (
      line ? { type: 'paragraph', content: [{ type: 'text', text: line }] } : { type: 'paragraph' }
    )),
  };
}

/** 문서에 글자·표·그림이 하나라도 있나 (제출 전 화면 안내용 — 판정은 서버가 한다) */
export function docIsEmpty(doc: RichDoc | null | undefined): boolean {
  if (!doc?.content?.length) return true;
  let has = false;
  for (const n of doc.content) {
    walk(n, (node) => {
      if (node.type === 'image' || node.type === 'table') has = true;
      if (node.type === 'text' && node.text && node.text.trim()) has = true;
      return node;
    });
    if (has) return false;
  }
  return true;
}

/** 문서의 글자만 이어 붙인다 (메일 발송 기록의 `body_text` 용 · 서버도 같은 규칙으로 다시 만든다) */
export function docToPlainText(doc: RichDoc): string {
  const lines: string[] = [];
  const inline = (nodes?: RichNode[]): string => (nodes ?? [])
    .map((n) => (n.type === 'text' ? n.text ?? '' : n.type === 'hardBreak' ? '\n' : '')).join('');
  const visit = (nodes: RichNode[], prefix: string): void => {
    for (const n of nodes) {
      if (n.type === 'paragraph' || n.type === 'heading') lines.push(prefix + inline(n.content));
      else if (n.type === 'listItem') {
        const [first, ...rest] = n.content ?? [];
        if (first && (first.type === 'paragraph' || first.type === 'heading')) {
          lines.push(`${prefix}• ${inline(first.content)}`);
          visit(rest, `${prefix}  `);
        } else visit(n.content ?? [], `${prefix}  `);
      } else if (n.type === 'bulletList' || n.type === 'orderedList') visit(n.content ?? [], prefix);
    }
  };
  visit(doc.content, '');
  return lines.join('\n').trim();
}

/** 문서 안 그림(새 것·저장된 것) 수 — 상한 안내용 */
export function countImages(doc: RichDoc): number {
  let n = 0;
  for (const top of doc.content) walk(top, (node) => { if (node.type === 'image') n += 1; return node; });
  return n;
}

/** 문서 안 그림의 **번호** 목록 (저장된 그림만 — 아직 안 올린 그림은 0 이라 뺀다) */
export function imageIdsOf(doc: RichDoc | null | undefined): number[] {
  const out: number[] = [];
  for (const top of doc?.content ?? []) {
    walk(top, (node) => {
      if (node.type === 'image') {
        const id = Number(node.attrs?.imageId) || 0;
        if (id > 0) out.push(id);
      }
      return node;
    });
  }
  return out;
}
